import React, { useCallback } from 'react';
import { Box, TextFieldProps, Typography } from '@mui/material';
import { useCurrentuser } from '@uplift/contexts';
import { ProviderMedicalRole } from '@uplift/types';
import { AppointmentDurationMappingDurationMinutes } from '../../../../Calendar';
import { MobileSection } from '../../../../Form';
import { AppointmentRepetitionSelect } from '../components/AppointmentRepetitionSelect.components';
import { AppointmentDateInput } from '../components/AppointmentDateInput.component';
import { StartTimeSelect } from '../components/StartTimeSelect';
import { UserInfo } from '../components/UserInfo';
import { InvitationAlert } from '../components/InvitationAlert.components';
import { useFormContext } from '../AddSessionForm.types';
import { SlotDurationInput } from '../components/SlotDurationInput.component';
import { ExistingOrInviteToggle } from '../components/ExistingOrInviteToggle.component';
import { NotesInput } from '../components/NotesInput.component';
import { AddSessionStatusAlert } from '../components/AddSessionStatusAlert.component';
import { useSessionFormFieldsSync } from '../components/form.hooks';
import { useAvailableSlots } from '../hooks';
import { AppointmentSlotDuration } from '@uplift/api';
import { usePickerContext } from '@mui/x-date-pickers';
import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material';


export type AddSessionFormMobileProps = {
  open?: boolean;
  newEvent?: { start?: string };
  showInviteToggle?: boolean;
  showPatientPicker?: boolean;
};


const DateInputComponent: React.FC<TextFieldProps> = () => {
  const pickerContext = usePickerContext();
  const arrowIcon = React.useMemo(() => pickerContext.open ? <ArrowDropUp color="action" /> : <ArrowDropDown color="action" />, [ pickerContext.open ]);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingRight: '7px',
        height: '48px',
        cursor: 'pointer'
      }}
      onClick={() => {
        pickerContext.setOpen(true);
      }}
      ref={pickerContext.triggerRef}
    >
      <Typography component="span" >
        {pickerContext.value == null
          ? ''
          : pickerContext.value.format(pickerContext.fieldFormat)
        }
      </Typography>
      {arrowIcon}
    </Box>
  );
};

export const AddSessionFormMobile: React.VFC<AddSessionFormMobileProps> = ({ showInviteToggle = true, showPatientPicker = true }) => {
  const currentUser = useCurrentuser();
  const formikContext = useFormContext();
  const isPsychiatrist = currentUser.medicalRole === ProviderMedicalRole.psychiatrist;
  const isTherapist = currentUser.medicalRole === ProviderMedicalRole.therapist;

  const dateFormatted = React.useMemo(() => formikContext.values.date, [ formikContext.values.date ]);
  const appointmentDuration = React.useMemo(() => {
    if (formikContext.values.duration) {
      return AppointmentDurationMappingDurationMinutes[formikContext.values.duration];
    }

    return AppointmentDurationMappingDurationMinutes.sixty;
  }, [ formikContext.values.duration ]);
  const allAvailableSlots = useAvailableSlots(dateFormatted, appointmentDuration);

  useSessionFormFieldsSync();

  const handleInviteToggleChange = useCallback((isNewPatient: boolean) => {
    if (!isPsychiatrist) {
      return;
    }

    if (isNewPatient === formikContext.values.isNewPatient) {
      return;
    }

    if (isNewPatient) {
      formikContext.setFieldValue('duration', AppointmentSlotDuration.sixty);
    } else {
      formikContext.setFieldValue('duration', AppointmentSlotDuration.thirty);
    }
  }, [ formikContext, isPsychiatrist ]);

  return (
    <Box display="flex" flexDirection="column">
      <Box px={2} pb={2}>
        <AddSessionStatusAlert />
      </Box>
      {(showInviteToggle || showPatientPicker) && (
        <Box px={2} pb={2} display="flex" flexDirection="column" gap="16px">
          {showInviteToggle && <ExistingOrInviteToggle name="isNewPatient" onChange={handleInviteToggleChange} />}

          <InvitationAlert />

          {showPatientPicker && <UserInfo />}
        </Box>
      )}

      <Box display="flex" flexDirection="column" gap="16px">
        <MobileSection label="Date" divider>
          <AppointmentDateInput name="date" TextFieldComponent={DateInputComponent} />
        </MobileSection>

        <MobileSection label="Start time" divider>
          <StartTimeSelect
            medicalRole={currentUser.medicalRole ?? ProviderMedicalRole.therapist}
            data-testid="start-time-select"
            textOnly
            name="startTime"
            appointmentDuration={appointmentDuration}
            slots={allAvailableSlots}
          />
        </MobileSection>


        <MobileSection label="Duration" divider>
          <SlotDurationInput
            data-testid="duration-select"
            name="duration"
            startTime={formikContext.values.startTime}
            slots={allAvailableSlots}
            medicalRole={currentUser.medicalRole ?? ProviderMedicalRole.therapist}
          />
        </MobileSection>

        {isTherapist && (
          <MobileSection label="Repeats" divider>
            <AppointmentRepetitionSelect name="repeats" data-testid="repetition-select" />
          </MobileSection>
        )}

        <MobileSection label="Location" divider>
          <Box display="flex" alignItems="center" style={{ cursor: 'default', paddingTop: '2px' }}>
            <Typography variant="body1">
              Virtual (Video and Audio)
            </Typography>
          </Box>
        </MobileSection>

        <MobileSection divider>
          <Box width="100%">
            <NotesInput name="notes" variant="mobile" color="secondary" />
          </Box>
        </MobileSection>
      </Box>
    </Box>
  );
};
